import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip, EmptyState } from "@/components/Shared";
import { MagnifyingGlass, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import IssuePassDialog from "./IssuePassDialog";
import { PASS_STATUS_VARIANTS } from "@/lib/parkingTypes";

export default function ParkingPassesTab({ slots }) {
    const [passes, setPasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [issueOpen, setIssueOpen] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/parking/passes");
            setPasses(data || []);
        } catch (e) { toast.error(formatError(e)); }
        setLoading(false);
    }
    useEffect(() => { load(); }, []);

    async function updateStatus(p, status) {
        if (status === "cancelled") {
            const reason = window.prompt(`Reason for cancelling pass ${p.pass_number}?`, "Cancelled by admin");
            if (reason === null) return;
            try {
                await api.patch(`/parking/passes/${p.id}`, { status, cancel_reason: reason });
                toast.success("Pass cancelled");
                load();
            } catch (e) { toast.error(formatError(e)); }
            return;
        }
        try {
            await api.patch(`/parking/passes/${p.id}`, { status });
            toast.success(`Pass marked ${status}`);
            load();
        } catch (e) { toast.error(formatError(e)); }
    }

    const filtered = useMemo(() => {
        let rows = passes;
        if (statusFilter !== "all") rows = rows.filter((p) => p.status === statusFilter);
        const term = search.trim().toLowerCase();
        if (term) {
            rows = rows.filter((p) => {
                const flatLabel = p.flat ? `${p.flat.block}-${p.flat.number}` : "";
                return p.pass_number.toLowerCase().includes(term) || flatLabel.toLowerCase().includes(term)
                    || (p.vehicle_registration_number || "").toLowerCase().includes(term) || (p.sticker_number || "").toLowerCase().includes(term);
            });
        }
        return rows;
    }, [passes, search, statusFilter]);

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3 bg-white border border-brand-line rounded-sm p-4 flex-1 mr-4">
                    <MagnifyingGlass size={18} className="text-brand-inkSoft" />
                    <Input data-testid="passes-search-input" placeholder="Search pass no, flat, vehicle or sticker…" value={search}
                        onChange={(e) => setSearch(e.target.value)} className="border-0 shadow-none focus-visible:ring-0 px-0" />
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger data-testid="passes-status-filter" className="w-44 rounded-sm border-brand-line shrink-0"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="expired">Expired</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <Button data-testid="issue-pass-btn" onClick={() => setIssueOpen(true)} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white shrink-0">
                    <Plus size={16} className="mr-2" /> Issue pass
                </Button>
            </div>
            {loading ? (
                <p className="text-sm text-brand-inkSoft py-6">Loading passes…</p>
            ) : filtered.length === 0 ? (
                <EmptyState title="No parking passes found" description="Issue a pass for a flat with an allotted parking slot." />
            ) : (
                <div className="overflow-x-auto bg-white border border-brand-line rounded-sm">
                    <table className="w-full text-sm" data-testid="passes-table">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-overline text-brand-inkSoft border-b border-brand-line">
                                <th className="py-3 px-4">Pass no.</th>
                                <th className="py-3 px-4">Flat</th>
                                <th className="py-3 px-4">Vehicle</th>
                                <th className="py-3 px-4">Sticker</th>
                                <th className="py-3 px-4">Issued</th>
                                <th className="py-3 px-4">Expiry</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((p) => (
                                <tr key={p.id} data-testid={`pass-row-${p.id}`} className="border-b border-brand-line/60 last:border-0">
                                    <td className="py-3 px-4 font-mono font-medium text-brand-ink">{p.pass_number}</td>
                                    <td className="py-3 px-4">{p.flat ? `${p.flat.block}-${p.flat.number}` : "—"}</td>
                                    <td className="py-3 px-4 text-brand-inkSoft">{p.vehicle_registration_number || "—"}</td>
                                    <td className="py-3 px-4 text-brand-inkSoft">{p.sticker_number || "—"}</td>
                                    <td className="py-3 px-4">{p.issue_date}</td>
                                    <td className="py-3 px-4">{p.expiry_date || "—"}</td>
                                    <td className="py-3 px-4"><Chip variant={PASS_STATUS_VARIANTS[p.status] || "neutral"}>{p.status}</Chip></td>
                                    <td className="py-3 px-4 text-right space-x-1">
                                        {p.status === "active" && (
                                            <>
                                                <Button size="sm" variant="outline" data-testid={`pass-expire-btn-${p.id}`} onClick={() => updateStatus(p, "expired")} className="rounded-full border-brand-line text-xs h-7">Mark expired</Button>
                                                <Button size="sm" variant="outline" data-testid={`pass-cancel-btn-${p.id}`} onClick={() => updateStatus(p, "cancelled")} className="rounded-full border-red-300 text-red-600 hover:bg-red-50 text-xs h-7">Cancel</Button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <IssuePassDialog open={issueOpen} onOpenChange={setIssueOpen} slots={slots} onSaved={load} />
        </div>
    );
}
